import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../App';
import Modal from '../components/UI/Modal';
import StatusPill from '../components/UI/StatusPill';
import { money } from '../utils/format';
import { hasPermission, canViewCosts } from '../utils/permissions';
import ProductDetail from '../components/UI/ProductDetail';
import { buildQuickOrderPrefill } from '../utils/quickOrder';
import {
  RefreshCw, MapPin, User, Calendar, Clock,
  ClipboardList, Package, PackageCheck, ArrowLeftRight, Plus,
  Eye, AlertTriangle, Search, TrendingDown, Trash2,
  ShoppingCart, BarChart3, ChevronRight, Activity,
  CheckSquare, ListChecks, Shield, FileCheck
} from 'lucide-react';

const relTime = ts => {
  if (!ts) return '';
  const d = Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
  if (d === 0) return 'Today';
  if (d === 1) return 'Yesterday';
  if (d < 7) return d + 'd ago';
  return new Date(ts).toLocaleDateString();
};

export default function Operations() {
  const { user } = useAuth();
  const nav = useNavigate();

  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Summary data
  const [summary, setSummary] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [pendingPOs, setPendingPOs] = useState([]);
  const [pendingTransfers, setPendingTransfers] = useState([]);

  // Tasks
  const [tasks, setTasks] = useState([]);
  const [taskSummary, setTaskSummary] = useState({ due_today: 0, overdue: 0, compliance_due: 0, completed_today: 0 });
  const [taskDetail, setTaskDetail] = useState(null);
  const [taskCompletionNotes, setTaskCompletionNotes] = useState('');

  // Modals
  const [searchModal, setSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [viewProductId, setViewProductId] = useState(null);
  const [countModal, setCountModal] = useState(null);
  const [countQty, setCountQty] = useState('');
  const [countNotes, setCountNotes] = useState('');
  const [wasteModal, setWasteModal] = useState(null);
  const [wasteQty, setWasteQty] = useState('');
  const [wasteReason, setWasteReason] = useState('breakage');
  const [wasteNotes, setWasteNotes] = useState('');

  // Resolve user's location
  useEffect(() => {
    api('/api/locations').then(r => {
      const locs = r.data || [];
      setLocations(locs);
      const userLocs = (user?.locations || []);
      if (userLocs.length === 1) {
        setSelectedLocation(locs.find(l => l.id === userLocs[0]) || locs[0]);
      } else if (userLocs.length > 1) {
        setSelectedLocation(locs.find(l => l.id === userLocs[0]) || locs[0]);
      } else if (locs.length > 0) {
        setSelectedLocation(locs[0]);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Load data when location changes
  useEffect(() => {
    if (selectedLocation) loadData();
  }, [selectedLocation]);

  const loadData = async () => {
    if (!selectedLocation) return;
    setRefreshing(true);
    const locId = selectedLocation.id;
    try {
      const [invRes, dashRes, poRes, trRes, taskRes, taskSumRes] = await Promise.all([
        api('/api/inventory?location_id=' + locId).catch(() => ({ data: [] })),
        api('/api/reports/dashboard?location_id=' + locId).catch(() => ({ data: null })),
        api('/api/orders?location_id=' + locId).catch(() => ({ data: [] })),
        api('/api/transfers?location_id=' + locId).catch(() => ({ data: [] })),
        api('/api/tasks?location_id=' + locId).catch(() => ({ data: [] })),
        api('/api/tasks/summary?location_id=' + locId).catch(() => ({ data: {} })),
      ]);
      setInventory((invRes.data || []).filter(i => i && i.product_name));
      setSummary(dashRes.data || null);
      setRecentActivity((dashRes.data?.recent_activity || []).slice(0, 8));
      setPendingPOs((poRes.data || []).filter(o => o && ['sent', 'confirmed'].includes(o.status)));
      setPendingTransfers((trRes.data || []).filter(t => t && ['pending', 'approved', 'in_transit'].includes(t.status)));
      setTasks((taskRes.data || []).filter(t => t && ['pending', 'in_progress', 'overdue'].includes(t.status)));
      setTaskSummary(taskSumRes.data || { due_today: 0, overdue: 0, compliance_due: 0, completed_today: 0 });
    } catch (e) {
      console.error('Operations load error', e);
    }
    setRefreshing(false);
  };

  // Computed summaries (filter out null/undefined items defensively)
  const reorderItems = useMemo(() => inventory.filter(i => i && i.product_name && i.stock_status === 'reorder'), [inventory]);
  const lowItems = useMemo(() => inventory.filter(i => i && i.product_name && (i.stock_status === 'low' || i.stock_status === 'reorder')), [inventory]);
  const countsDue = useMemo(() => {
    const threeDaysAgo = Date.now() - 3 * 86400000;
    return inventory.filter(i => !i.last_counted_at || new Date(i.last_counted_at).getTime() < threeDaysAgo);
  }, [inventory]);

  // Product search
  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    const q = searchQuery.toLowerCase();
    const results = inventory.filter(i =>
      (i.product_name || '').toLowerCase().includes(q) ||
      (i.sku || '').toLowerCase().includes(q) ||
      (i.supplier_name || '').toLowerCase().includes(q)
    ).slice(0, 15);
    setSearchResults(results);
  }, [searchQuery, inventory]);

  // Count submit
  const submitCount = async () => {
    if (!countModal) return;
    try {
      await api('/api/inventory/count', {
        method: 'POST',
        body: JSON.stringify({
          product_location_id: countModal.id,
          count_qty: parseFloat(countQty),
          notes: countNotes
        })
      });
      setCountModal(null);
      setCountQty('');
      setCountNotes('');
      loadData();
    } catch (e) { alert(e.error || 'Count failed'); }
  };

  // Waste submit
  const submitWaste = async () => {
    if (!wasteModal) return;
    try {
      await api('/api/inventory/waste', {
        method: 'POST',
        body: JSON.stringify({
          product_location_id: wasteModal.id,
          quantity: parseFloat(wasteQty),
          reason: wasteReason,
          notes: wasteNotes
        })
      });
      setWasteModal(null);
      setWasteQty('');
      setWasteNotes('');
      loadData();
    } catch (e) { alert(e.error || 'Waste log failed'); }
  };

  // Quick order — navigate to Orders with prefilled state
  const quickOrder = (item) => {
    nav('/orders', {
      state: { prefill: buildQuickOrderPrefill(item, selectedLocation?.id) }
    });
  };

  // Task actions
  const startTask = async (id) => {
    try {
      await api('/api/tasks/' + id + '/start', { method: 'PUT' });
      loadData();
    } catch (e) { alert(e.error || 'Failed'); }
  };

  const completeTask = async (id) => {
    try {
      const task = tasks.find(t => t.id === id) || taskDetail;
      const checklist = task?.checklist_items ? (typeof task.checklist_items === 'string' ? JSON.parse(task.checklist_items) : task.checklist_items) : null;
      await api('/api/tasks/' + id + '/complete', {
        method: 'PUT',
        body: JSON.stringify({ completion_notes: taskCompletionNotes, checklist_items: checklist })
      });
      setTaskDetail(null);
      setTaskCompletionNotes('');
      loadData();
    } catch (e) { alert(e.error || 'Failed'); }
  };

  const toggleChecklistItem = async (taskId, idx) => {
    const task = tasks.find(t => t.id === taskId) || taskDetail;
    if (!task) return;
    const items = typeof task.checklist_items === 'string' ? JSON.parse(task.checklist_items) : [...(task.checklist_items || [])];
    items[idx] = { ...items[idx], checked: !items[idx].checked, checked_at: new Date().toISOString(), checked_by: user?.name };
    try {
      await api('/api/tasks/' + taskId + '/checklist', { method: 'PUT', body: JSON.stringify({ checklist_items: items }) });
      // Update local state
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, checklist_items: items } : t));
      if (taskDetail?.id === taskId) setTaskDetail({ ...taskDetail, checklist_items: items });
    } catch (e) { console.error('Failed to update checklist'); }
  };

  const userLocations = useMemo(() => {
    const ids = user?.locations || [];
    return locations.filter(l => ids.includes(l.id));
  }, [locations, user]);

  const showCosts = canViewCosts(user);
  const canCount = hasPermission(user, 'count_inventory');
  const canWasteLog = hasPermission(user, 'log_waste');
  const canCreatePO = hasPermission(user, 'create_po_direct');
  const canReceivePO = hasPermission(user, 'receive_po');
  const canTransfer = hasPermission(user, 'create_transfer');
  const canAddProduct = hasPermission(user, 'edit_products');

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  if (loading) {
    return (
      <div className="ops-loading">
        <RefreshCw size={28} className="spin" style={{ color: 'var(--gold)' }} />
        <span>Loading operations...</span>
      </div>
    );
  }

  return (
    <div className="ops-page">
      {/* ── HEADER ── */}
      <div className="ops-header">
        <div className="ops-header-left">
          <h1 className="ops-title">Operations</h1>
          <div className="ops-meta">
            <span className="ops-meta-item"><User size={14} /> {user?.name}</span>
            <span className="ops-meta-item"><MapPin size={14} /> {selectedLocation?.name || 'No location'}</span>
            <span className="ops-meta-item"><Calendar size={14} /> {today}</span>
          </div>
        </div>
        <div className="ops-header-right">
          {userLocations.length > 1 && (
            <select
              className="ops-loc-select"
              value={selectedLocation?.id || ''}
              onChange={e => {
                const loc = locations.find(l => l.id === Number(e.target.value));
                if (loc) setSelectedLocation(loc);
              }}
            >
              {userLocations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          )}
          <button className="ops-refresh-btn" onClick={loadData} disabled={refreshing}>
            <RefreshCw size={18} className={refreshing ? 'spin' : ''} />
          </button>
        </div>
      </div>

      {/* ── SUMMARY CARDS ── */}
      <div className="ops-summary-row">
        <SummaryCard
          label="Reorder Items"
          value={reorderItems.length}
          color="var(--danger)"
          urgent={reorderItems.length > 0}
          onClick={() => nav('/inventory?location_id=' + selectedLocation?.id + '&status=reorder')}
        />
        <SummaryCard
          label="Counts Due"
          value={countsDue.length}
          color="var(--warning)"
          urgent={countsDue.length > 5}
          onClick={() => nav('/inventory?location_id=' + selectedLocation?.id)}
        />
        <SummaryCard
          label="Pending Receipts"
          value={pendingPOs.length}
          color="var(--c-bev)"
          onClick={() => nav('/orders?status=confirmed')}
        />
        <SummaryCard
          label="Pending Transfers"
          value={pendingTransfers.length}
          color="var(--c-equip)"
          onClick={() => nav('/transfers')}
        />
        <SummaryCard
          label="Tasks Due"
          value={taskSummary.due_today + taskSummary.overdue}
          color="var(--c-co2)"
          urgent={taskSummary.overdue > 0}
          onClick={() => nav('/tasks')}
        />
        <SummaryCard
          label="Compliance Due"
          value={taskSummary.compliance_due}
          color="var(--warning)"
          urgent={taskSummary.compliance_due > 0}
          onClick={() => nav('/tasks')}
        />
      </div>

      {/* ── ACTION GRID ── */}
      <div className="ops-actions-section">
        <h2 className="ops-section-title">Quick Actions</h2>
        <div className="ops-action-grid">
          {canCreatePO && <ActionTile icon={ClipboardList} label="New PO" color="var(--gold)" onClick={() => nav('/orders', { state: { prefill: { location_id: selectedLocation?.id } } })} />}
          {canReceivePO && <ActionTile icon={PackageCheck} label="Receive" color="var(--success)" onClick={() => nav('/orders?status=confirmed')} />}
          {canTransfer && <ActionTile icon={ArrowLeftRight} label="Transfer" color="var(--c-equip)" onClick={() => nav('/transfers')} />}
          {canCount && <ActionTile icon={BarChart3} label="Count" color="var(--c-bev)" onClick={() => nav('/inventory?location_id=' + selectedLocation?.id)} />}
          {canAddProduct && <ActionTile icon={Plus} label="Add Product" color="var(--c-food)" onClick={() => nav('/products', { state: { openAdd: true, defaultLocation: selectedLocation?.id } })} />}
          <ActionTile icon={Eye} label="View Inventory" color="var(--c-paper)" onClick={() => nav('/inventory?location_id=' + selectedLocation?.id)} />
          <ActionTile icon={AlertTriangle} label="Low Stock" color="var(--danger)" onClick={() => nav('/inventory?location_id=' + selectedLocation?.id + '&status=reorder')} />
          <ActionTile icon={Search} label="Search Product" color="var(--c-clean)" onClick={() => { setSearchModal(true); setSearchQuery(''); setSearchResults([]); }} />
          <ActionTile icon={Activity} label="Recent Activity" color="var(--c-co2)" onClick={() => nav('/reports')} />
          {canWasteLog && <ActionTile icon={Trash2} label="Report Waste" color="var(--warning)" onClick={() => nav('/inventory?location_id=' + selectedLocation?.id)} />}
          <ActionTile icon={CheckSquare} label="My Tasks" color="var(--c-co2)" onClick={() => nav('/tasks')} />
          <ActionTile icon={ListChecks} label="Checklists" color="var(--c-uniform)" onClick={() => { if (tasks.length > 0) setTaskDetail(tasks.find(t => t.checklist_items) || tasks[0]); else nav('/tasks'); }} />
        </div>
      </div>

      {/* ── MY TASKS ── */}
      {tasks.length > 0 && (
        <div className="ops-attention-section">
          <h2 className="ops-section-title">My Tasks</h2>
          <div className="ops-attention-grid">
            {/* Active Tasks */}
            <div className="ops-attention-card">
              <div className="ops-attention-header">
                <CheckSquare size={16} style={{ color: 'var(--c-co2)' }} />
                <span>Tasks ({tasks.filter(t => !t.is_compliance).length})</span>
              </div>
              <div className="ops-attention-list">
                {tasks.filter(t => !t.is_compliance).slice(0, 6).map(task => {
                  const isOverdue = task.status === 'overdue' || (task.due_date && new Date(task.due_date) < new Date(new Date().toDateString()));
                  const checklist = task.checklist_items ? (typeof task.checklist_items === 'string' ? JSON.parse(task.checklist_items) : task.checklist_items) : null;
                  const progress = checklist ? { done: checklist.filter(i => i.checked).length, total: checklist.length } : null;
                  return (
                    <div key={task.id} className="ops-attention-item" style={{ cursor: 'pointer' }} onClick={() => setTaskDetail(task)}>
                      <div className="ops-attention-item-info">
                        <span className="ops-attention-name" style={{ color: isOverdue ? 'var(--danger)' : undefined }}>
                          {task.title}
                        </span>
                        <span className="ops-attention-detail">
                          {task.due_date && <>{isOverdue ? 'OVERDUE' : new Date(task.due_date).toLocaleDateString()}</>}
                          {task.due_time && <> {task.due_time.slice(0, 5)}</>}
                          {progress && <> &middot; {progress.done}/{progress.total}</>}
                        </span>
                      </div>
                      <div className="ops-attention-actions">
                        {task.status === 'pending' && <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); startTask(task.id); }}>Start</button>}
                        {task.status === 'in_progress' && <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); completeTask(task.id); }}>Done</button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Compliance Tasks */}
            {tasks.filter(t => t.is_compliance).length > 0 && (
              <div className="ops-attention-card">
                <div className="ops-attention-header">
                  <Shield size={16} style={{ color: 'var(--warning)' }} />
                  <span>Compliance ({tasks.filter(t => t.is_compliance).length})</span>
                </div>
                <div className="ops-attention-list">
                  {tasks.filter(t => t.is_compliance).slice(0, 6).map(task => {
                    const isOverdue = task.status === 'overdue' || (task.due_date && new Date(task.due_date) < new Date(new Date().toDateString()));
                    const checklist = task.checklist_items ? (typeof task.checklist_items === 'string' ? JSON.parse(task.checklist_items) : task.checklist_items) : null;
                    const progress = checklist ? { done: checklist.filter(i => i.checked).length, total: checklist.length } : null;
                    return (
                      <div key={task.id} className="ops-attention-item" style={{ cursor: 'pointer' }} onClick={() => setTaskDetail(task)}>
                        <div className="ops-attention-item-info">
                          <span className="ops-attention-name" style={{ color: isOverdue ? 'var(--danger)' : undefined }}>
                            {task.title}
                            <span style={{ marginLeft: 6, fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'rgba(243,156,18,.15)', color: 'var(--warning)', fontWeight: 700 }}>COMPLIANCE</span>
                          </span>
                          <span className="ops-attention-detail">
                            {task.due_date && <>{isOverdue ? 'OVERDUE' : new Date(task.due_date).toLocaleDateString()}</>}
                            {progress && <> &middot; {progress.done}/{progress.total}</>}
                          </span>
                        </div>
                        <div className="ops-attention-actions">
                          {task.status !== 'completed' && <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); setTaskDetail(task); }}>Open</button>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── NEEDS ATTENTION ── */}
      <div className="ops-attention-section">
        <h2 className="ops-section-title">Needs Attention</h2>
        <div className="ops-attention-grid">
          {/* Low Stock Items */}
          {reorderItems.length > 0 && (
            <div className="ops-attention-card">
              <div className="ops-attention-header">
                <AlertTriangle size={16} style={{ color: 'var(--danger)' }} />
                <span>Low Stock / Reorder ({reorderItems.length})</span>
              </div>
              <div className="ops-attention-list">
                {reorderItems.slice(0, 5).map(item => (
                  <div key={item.id} className="ops-attention-item">
                    <div className="ops-attention-item-info">
                      <span className="ops-attention-name">{item.product_name}</span>
                      <span className="ops-attention-detail">
                        Stock: {Number(item.current_stock).toFixed(0)} / Min: {Number(item.reorder_point).toFixed(0)}
                      </span>
                    </div>
                    <div className="ops-attention-actions">
                      {(item.default_supplier_id || item.supplier_id) && (
                        <button className="btn btn-primary btn-sm" onClick={() => quickOrder(item)}>Order</button>
                      )}
                      <button className="btn btn-secondary btn-sm" onClick={() => {
                        setCountModal(item);
                        setCountQty(String(item.current_stock || 0));
                      }}>Count</button>
                    </div>
                  </div>
                ))}
                {reorderItems.length > 5 && (
                  <button className="ops-attention-more" onClick={() => nav('/inventory?location_id=' + selectedLocation?.id + '&status=reorder')}>
                    View all {reorderItems.length} items <ChevronRight size={14} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Pending Receives */}
          {pendingPOs.length > 0 && (
            <div className="ops-attention-card">
              <div className="ops-attention-header">
                <PackageCheck size={16} style={{ color: 'var(--c-bev)' }} />
                <span>Pending Receipts ({pendingPOs.length})</span>
              </div>
              <div className="ops-attention-list">
                {pendingPOs.slice(0, 4).map(po => (
                  <div key={po.id} className="ops-attention-item" onClick={() => nav('/orders?status=' + po.status)} style={{ cursor: 'pointer' }}>
                    <div className="ops-attention-item-info">
                      <span className="ops-attention-name">{po.po_number}</span>
                      <span className="ops-attention-detail">{po.supplier_name} &middot; {showCosts ? money(po.total_amount) : '•••'}</span>
                    </div>
                    <StatusPill status={po.status} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pending Transfers */}
          {pendingTransfers.length > 0 && (
            <div className="ops-attention-card">
              <div className="ops-attention-header">
                <ArrowLeftRight size={16} style={{ color: 'var(--c-equip)' }} />
                <span>Pending Transfers ({pendingTransfers.length})</span>
              </div>
              <div className="ops-attention-list">
                {pendingTransfers.slice(0, 4).map(tr => (
                  <div key={tr.id} className="ops-attention-item" onClick={() => nav('/transfers')} style={{ cursor: 'pointer' }}>
                    <div className="ops-attention-item-info">
                      <span className="ops-attention-name">{tr.product_name || 'Transfer #' + tr.id}</span>
                      <span className="ops-attention-detail">
                        {tr.from_location_name} → {tr.to_location_name} &middot; Qty: {Number(tr.quantity).toFixed(0)}
                      </span>
                    </div>
                    <StatusPill status={tr.status} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {reorderItems.length === 0 && pendingPOs.length === 0 && pendingTransfers.length === 0 && (
            <div className="ops-attention-card" style={{ textAlign: 'center', padding: '32px 20px' }}>
              <Package size={32} style={{ color: 'var(--success)', marginBottom: 8 }} />
              <div style={{ color: 'var(--success)', fontWeight: 600, fontSize: 15 }}>All clear!</div>
              <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 4 }}>No items need attention right now.</div>
            </div>
          )}
        </div>
      </div>

      {/* ── RECENT ACTIVITY ── */}
      {recentActivity.length > 0 && (
        <div className="ops-activity-section">
          <h2 className="ops-section-title">Recent Activity</h2>
          <div className="ops-activity-list">
            {recentActivity.map((a, i) => (
              <div key={i} className="ops-activity-item">
                <div className="ops-activity-icon" style={{
                  background: a.type === 'order' ? 'rgba(74,158,191,0.15)' :
                    a.type === 'transfer' ? 'rgba(176,127,212,0.15)' :
                    a.type === 'count' ? 'rgba(123,196,127,0.15)' : 'var(--gold-pale)'
                }}>
                  {a.type === 'order' ? <ClipboardList size={14} style={{ color: 'var(--c-bev)' }} /> :
                   a.type === 'transfer' ? <ArrowLeftRight size={14} style={{ color: 'var(--c-equip)' }} /> :
                   a.type === 'count' ? <Package size={14} style={{ color: 'var(--c-paper)' }} /> :
                   <Activity size={14} style={{ color: 'var(--gold)' }} />}
                </div>
                <div className="ops-activity-content">
                  <span className="ops-activity-desc">{a.description}</span>
                  <span className="ops-activity-time">{relTime(a.timestamp)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── SEARCH PRODUCT MODAL ── */}
      {searchModal && (
        <Modal title="Search Products" onClose={() => setSearchModal(false)}>
          <div className="search-input" style={{ marginBottom: 16 }}>
            <Search size={16} />
            <input
              placeholder="Search by name, SKU, or supplier..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              autoFocus
              style={{ fontSize: 16, padding: '14px 14px 14px 40px' }}
            />
          </div>
          <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            {searchResults.length === 0 && searchQuery && (
              <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)' }}>No products found</div>
            )}
            {searchResults.map(item => (
              <div key={item.id} className="ops-search-result" onClick={() => { setViewProductId(item.product_id); }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{item.product_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {item.sku && <span>{item.sku} &middot; </span>}
                    Stock: {Number(item.current_stock).toFixed(0)} {item.unit_type}
                    {item.supplier_name && <span> &middot; {item.supplier_name}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <StatusPill status={item.stock_status || 'ok'} />
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {/* ── COUNT MODAL ── */}
      {countModal && (
        <Modal title="Log Count" onClose={() => setCountModal(null)}>
          <div style={{ padding: '12px 16px', background: 'var(--card)', borderRadius: 10, marginBottom: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{countModal.product_name}</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>{selectedLocation?.name}</div>
          </div>
          <div className="form-group">
            <label>Current Count</label>
            <input
              type="number"
              value={countQty}
              onChange={e => setCountQty(e.target.value)}
              min="0"
              step="0.1"
              autoFocus
              style={{ fontSize: 18, textAlign: 'center', padding: 14 }}
            />
          </div>
          <div className="form-group">
            <label>Notes (optional)</label>
            <textarea value={countNotes} onChange={e => setCountNotes(e.target.value)} rows={2} />
          </div>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setCountModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={submitCount} disabled={!countQty}>Save Count</button>
          </div>
        </Modal>
      )}

      {/* ── WASTE MODAL ── */}
      {wasteModal && (
        <Modal title="Report Waste" onClose={() => setWasteModal(null)}>
          <div style={{ padding: '12px 16px', background: 'var(--card)', borderRadius: 10, marginBottom: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{wasteModal.product_name}</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>{selectedLocation?.name}</div>
          </div>
          <div className="form-group">
            <label>Quantity Lost</label>
            <input type="number" value={wasteQty} onChange={e => setWasteQty(e.target.value)} min="0.1" step="0.1" autoFocus style={{ fontSize: 16, padding: 12 }} />
          </div>
          <div className="form-group">
            <label>Reason</label>
            <select value={wasteReason} onChange={e => setWasteReason(e.target.value)} style={{ fontSize: 15 }}>
              <option value="breakage">Breakage</option>
              <option value="expired">Expired</option>
              <option value="spilled">Spilled</option>
              <option value="stolen">Stolen / Missing</option>
              <option value="comp">Comp</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="form-group">
            <label>Notes (optional)</label>
            <textarea value={wasteNotes} onChange={e => setWasteNotes(e.target.value)} rows={2} />
          </div>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setWasteModal(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={submitWaste} disabled={!wasteQty}>Log Waste</button>
          </div>
        </Modal>
      )}

      {/* ── TASK DETAIL MODAL ── */}
      {taskDetail && (
        <Modal title={taskDetail.title} onClose={() => { setTaskDetail(null); setTaskCompletionNotes(''); }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
            {taskDetail.is_compliance && <span style={{ padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: 'rgba(243,156,18,.15)', color: 'var(--warning)' }}>COMPLIANCE</span>}
            <span style={{ padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: 'var(--card)', color: 'var(--muted)', textTransform: 'capitalize' }}>{taskDetail.task_type?.replace('_', ' ')}</span>
            <span style={{ padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: ({ urgent: '#E74C3C', high: '#F39C12', medium: '#78A8E8', low: '#4A6A8A' }[taskDetail.priority] || '#4A6A8A') + '1A', color: ({ urgent: '#E74C3C', high: '#F39C12', medium: '#78A8E8', low: '#4A6A8A' }[taskDetail.priority] || '#4A6A8A'), textTransform: 'uppercase' }}>{taskDetail.priority}</span>
            <StatusPill status={taskDetail.status} />
          </div>

          {taskDetail.description && <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>{taskDetail.description}</p>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16, fontSize: 13 }}>
            {taskDetail.location_name && <div><span style={{ color: 'var(--faint)' }}>Location:</span> <span style={{ fontWeight: 500 }}>{taskDetail.location_name}</span></div>}
            {taskDetail.due_date && <div><span style={{ color: 'var(--faint)' }}>Due:</span> <span style={{ fontWeight: 500, color: (taskDetail.status === 'overdue' || new Date(taskDetail.due_date) < new Date(new Date().toDateString())) ? 'var(--danger)' : undefined }}>{new Date(taskDetail.due_date).toLocaleDateString()}{taskDetail.due_time ? ' ' + String(taskDetail.due_time).slice(0, 5) : ''}</span></div>}
            {taskDetail.assigned_user_name && <div><span style={{ color: 'var(--faint)' }}>Assigned to:</span> <span style={{ fontWeight: 500 }}>{taskDetail.assigned_user_name}</span></div>}
            {taskDetail.created_by_name && <div><span style={{ color: 'var(--faint)' }}>Created by:</span> <span style={{ fontWeight: 500 }}>{taskDetail.created_by_name}</span></div>}
          </div>

          {/* Checklist */}
          {(() => {
            const items = taskDetail.checklist_items ? (typeof taskDetail.checklist_items === 'string' ? JSON.parse(taskDetail.checklist_items) : taskDetail.checklist_items) : null;
            if (!items || !items.length) return null;
            const done = items.filter(i => i.checked).length;
            return (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Checklist</span>
                  <span style={{ fontSize: 12, color: done === items.length ? 'var(--success)' : 'var(--muted)' }}>{done}/{items.length}</span>
                </div>
                <div style={{ width: '100%', height: 4, borderRadius: 2, background: 'var(--navy)', marginBottom: 10, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: (done / items.length * 100) + '%', background: done === items.length ? 'var(--success)' : 'var(--gold)', borderRadius: 2, transition: 'width 0.3s' }} />
                </div>
                {items.map((item, idx) => (
                  <div key={idx} onClick={() => taskDetail.status !== 'completed' && toggleChecklistItem(taskDetail.id, idx)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid var(--border)', cursor: taskDetail.status !== 'completed' ? 'pointer' : 'default', transition: 'background 0.15s' }}
                    onMouseEnter={e => { if (taskDetail.status !== 'completed') e.currentTarget.style.background = 'var(--hover-row)'; }}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ width: 22, height: 22, borderRadius: 6, border: '2px solid ' + (item.checked ? 'var(--success)' : 'var(--faint)'), background: item.checked ? 'var(--success)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s' }}>
                      {item.checked && <CheckSquare size={14} style={{ color: '#fff' }} />}
                    </div>
                    <span style={{ fontSize: 14, textDecoration: item.checked ? 'line-through' : 'none', color: item.checked ? 'var(--faint)' : 'var(--text)' }}>
                      {item.label}
                      {item.required && <span style={{ color: 'var(--danger)', marginLeft: 2 }}>*</span>}
                    </span>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Completion section */}
          {taskDetail.status === 'completed' && (
            <div style={{ padding: '12px 14px', background: 'rgba(46,204,113,.08)', border: '1px solid rgba(46,204,113,.2)', borderRadius: 10, marginBottom: 16, fontSize: 13 }}>
              <div style={{ color: 'var(--success)', fontWeight: 600, marginBottom: 4 }}>Completed</div>
              {taskDetail.completed_by_name && <div style={{ color: 'var(--muted)' }}>By: {taskDetail.completed_by_name}</div>}
              {taskDetail.completed_at && <div style={{ color: 'var(--muted)' }}>{new Date(taskDetail.completed_at).toLocaleString()}</div>}
              {taskDetail.completed_on_time !== null && <div style={{ color: taskDetail.completed_on_time ? 'var(--success)' : 'var(--danger)' }}>{taskDetail.completed_on_time ? 'On time' : 'Late'}</div>}
              {taskDetail.completion_notes && <div style={{ color: 'var(--muted)', marginTop: 6 }}>{taskDetail.completion_notes}</div>}
            </div>
          )}

          {/* Action buttons */}
          {taskDetail.status !== 'completed' && taskDetail.status !== 'cancelled' && (
            <div>
              {taskDetail.status !== 'in_progress' && (
                <div className="form-group" style={{ marginBottom: 12 }}>
                  <label>Completion Notes (optional)</label>
                  <textarea value={taskCompletionNotes} onChange={e => setTaskCompletionNotes(e.target.value)} rows={2} placeholder="Any notes about this task..." />
                </div>
              )}
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setTaskDetail(null)}>Close</button>
                {taskDetail.status === 'pending' && <button className="btn btn-secondary" onClick={() => startTask(taskDetail.id)}>Start Task</button>}
                <button className="btn btn-primary" onClick={() => completeTask(taskDetail.id)}>Complete Task</button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {viewProductId && <ProductDetail productId={viewProductId} onClose={() => setViewProductId(null)} />}
    </div>
  );
}

/* ── Sub-components ── */

function SummaryCard({ label, value, color, urgent, onClick }) {
  return (
    <div
      className={'ops-summary-card' + (urgent ? ' ops-urgent' : '')}
      onClick={onClick}
      style={{ '--accent': color }}
    >
      <div className="ops-summary-value" style={{ color }}>{value}</div>
      <div className="ops-summary-label">{label}</div>
    </div>
  );
}

function ActionTile({ icon: Icon, label, color, onClick }) {
  return (
    <button className="ops-action-tile" onClick={onClick} style={{ '--tile-color': color }}>
      <div className="ops-tile-icon">
        <Icon size={26} />
      </div>
      <span className="ops-tile-label">{label}</span>
    </button>
  );
}
